import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import Loader from '../shared/Loader';
import ZeroState from 'shared/ZeroState';
import { FontSizes, Heading } from '@hexure/ui';
import { RATE_TYPES } from 'constants/App';

const Container = styled.div`
  display: flex;
  max-height: 545px;
  overflow-x: auto;
  overflow-y: auto;
  flex-direction: column;
  padding: 40px;
`;

const SectionTitle = styled.h4`
  font-size: ${FontSizes.Medium};
  margin: 24px 0 8px;
  font-weight: 450;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  margin-bottom: 32px;
  border: 1px solid #e5e5e5;
`;

const TableHeader = styled.th`
  text-align: center;
  padding: 12px;
  border: 1px solid #ddd;
  background-color: #f9f9f9;
  font-size: ${FontSizes.SMALL};
`;

const TableCell = styled.td`
  padding: 12px;
  border: 1px solid #ddd;
  font-size: ${FontSizes.DEFAULT};
  max-width: 50%;
  word-wrap: break-word;
  overflow-wrap: break-word;
  text-align: center;
  vertical-align: center;
`;
// Helper function to round rates to nearest 0.05%
const roundToNearestFiveHundredths = rate => {
  if (!rate && rate !== 0) return 0;
  const numericRate = parseFloat(rate);
  if (isNaN(numericRate)) return 0;
  // Round to nearest 0.05% (since values are already percentages)
  const rounded = Math.round(numericRate / 0.05) * 0.05;
  return rounded;
};
const getRateInfo = item => {
  for (const rateType of RATE_TYPES) {
    if (item[rateType.key] !== null && item[rateType.key] !== undefined) {
      const roundedRate = roundToNearestFiveHundredths(item[rateType.key]);
      return [rateType.label, `${roundedRate.toFixed(2)}%`];
    }
  }
  return ['N/A', 'N/A'];
};

const RatesInfo = ({ data = [], loading }) => {
  // Flatten all rates arrays from each object in data
  const list = Array.isArray(data) ? data.flatMap(obj => (Array.isArray(obj.rates) ? obj.rates : [])) : [];
  const byIndex = list.reduce((acc, item) => {
    const idx = item.index_name || 'N/A';
    if (!acc[idx]) acc[idx] = [];
    acc[idx].push(item);
    return acc;
  }, {});

  const entries = Object.entries(byIndex);

  const columns = [
    {
      key: 'marketing_name',
      label: 'Name',
      getValue: item => item.marketing_name || 'N/A'
    },
    {
      key: 'rateType',
      label: 'Rate Type',
      getValue: item => getRateInfo(item)[0] || 'N/A'
    },
    {
      key: 'rateValue',
      label: 'Rate',
      getValue: item => getRateInfo(item)[1] || 'N/A'
    },
    {
      key: 'strategy_name',
      label: 'Crediting Strategy',
      getValue: item => item.strategy_name || 'N/A'
    },
    {
      key: 'crediting_frequency',
      label: 'Duration',
      getValue: item => item.crediting_frequency || 'N/A'
    },
    {
      key: 'description',
      label: 'Description',
      getValue: item => item.description || 'N/A'
    }
  ];

  const getVisibleColumns = items => {
    return columns.filter(col => {
      if (col.key === 'description') {
        return items.some(item => item.description && item.description.trim() !== '');
      }
      if (col.key === 'rateType' || col.key === 'rateValue') {
        return items.some(item => {
          const [type, value] = getRateInfo(item);
          return (col.key === 'rateType' ? type : value) && (col.key === 'rateType' ? type : value) !== 'N/A';
        });
      }
      return items.some(item => col.getValue(item) && col.getValue(item) !== 'N/A');
    });
  };

  return loading ? (
    <Loader />
  ) : (
    <Container>
      <Heading style={{ marginBottom: '15px' }} type='tertiary'>
        Crediting Strategies
      </Heading>
      {entries.length > 0 ? (
        entries.map(([indexName, items]) => {
          const visibleColumns = getVisibleColumns(items);
          return (
            <div key={indexName}>
              <SectionTitle>{indexName}</SectionTitle>
              <Table>
                <thead>
                  <tr>
                    {visibleColumns.map(col => (
                      <TableHeader key={col.key}>{col.label}</TableHeader>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, i) => (
                    <tr key={i}>
                      {visibleColumns.map(col => {
                        if (col.key === 'description') {
                          let descriptionContent;
                          if (item.description) {
                            if (item.description.length > 60) {
                              descriptionContent = (
                                <details style={{ cursor: 'pointer' }}>
                                  <summary>{item.description.slice(0, 60) + '…'}</summary>
                                  <p style={{ margin: '0.5em 0' }}>{item.description.slice(60)}</p>
                                </details>
                              );
                            } else {
                              descriptionContent = item.description;
                            }
                          } else {
                            descriptionContent = 'N/A';
                          }
                          return <TableCell key={col.key}>{descriptionContent}</TableCell>;
                        }
                        if (col.key === 'rateType' || col.key === 'rateValue') {
                          const [rateType, rateValue] = getRateInfo(item);
                          if (col.key === 'rateValue') {
                            // Show effective_date as tooltip on hover, formatted as MM/DD/YYYY
                            let formattedDate = undefined;
                            if (item.effective_date) {
                              const d = new Date(item.effective_date);
                              if (!isNaN(d)) {
                                const mm = String(d.getMonth() + 1).padStart(2, '0');
                                const dd = String(d.getDate()).padStart(2, '0');
                                const yyyy = d.getFullYear();
                                formattedDate = `${mm}/${dd}/${yyyy}`;
                              }
                            }
                            return (
                              <TableCell key={col.key} title={formattedDate ? `Effective Date: ${formattedDate}` : undefined}>
                                {rateValue}
                              </TableCell>
                            );
                          }
                          return <TableCell key={col.key}>{rateType}</TableCell>;
                        }
                        return <TableCell key={col.key}>{col.getValue(item)}</TableCell>;
                      })}
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          );
        })
      ) : (
        <ZeroState icon='mdi-magnify' message='No indexed rates available' />
      )}
    </Container>
  );
};

RatesInfo.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      index_name: PropTypes.string.isRequired,
      marketing_name: PropTypes.string.isRequired,
      strategy_name: PropTypes.string.isRequired,
      cap_rate: PropTypes.number,
      performance_trigger: PropTypes.number,
      participation_rate: PropTypes.number,
      spread_rate: PropTypes.number,
      fixed_rate: PropTypes.number,
      crediting_frequency: PropTypes.string,
      description: PropTypes.string
    })
  ),
  loading: PropTypes.bool
};

export default RatesInfo;
